import "./GameInput.scss";
import React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  toggleNotStart,
  updateGameStatus,
  updatePlayerWPM,
  updatePlayerWord,
  setPracticeWPM,
  setMultiplayerWPM,
  setProgressRaceStatistic,
  setIncorrectRaceStatistic,
  addLiveWPMDatapoint,
} from "../../redux/stores/GameStore";
import { socket } from "../../socket";
import { deserify, defaultOptions } from "@karmaniverous/serify-deserify";

const GameInput = React.forwardRef(
  (
    { words, baseIdx, progressCurrCharIdx, chars, charArray, setCharArray },
    inputElement,
  ) => {
    const { room, multiplayer_id, solo_id, notStart, countdownFlag } =
      useSelector((state) => state.game);
    const dispatch = useDispatch();
    const LIMIT = words.length;
    const roomType = room.gameType;
    const player_id = roomType === "PRACTICE" ? solo_id : multiplayer_id;
    // Guard: Ensure player exists before accessing properties
    const playerState =
      room.players && player_id ? room.players[player_id] : undefined;
    const gameEnd = playerState?.gameEnd === true;
    const [b_idx, setBase] = useState(baseIdx);
    const [practiceStartTime, setPracticeStart] = useState("");
    const [inputLength, setInputLength] = useState(0);
    const [, setInputValue] = useState("");
    let { gameType, mStartTime } = room;

    // Rolling window WPM tracking
    const typingEventsRef = useRef([]); // Ring buffer: [{timestampMs, deltaChars}, ...]
    const raceStartTimeRef = useRef(null); // Timestamp when race actually starts (ms)
    const liveWPMUpdateIntervalRef = useRef(null);
    const WPM_WINDOW_MS = 2000; // 2 second window
    const WPM_UPDATE_INTERVAL_MS = 150; // Update every 150ms
    
    // Cumulative WPM tracking (total characters typed / total time)
    const totalCharsTypedRef = useRef(0); // Total characters typed since race start

    const handleInputChange = (e) => {
      // Guard: Early return if player state is not ready
      if (!playerState || typeof playerState.currentWord !== "number") {
        return;
      }
      const l = e.target.value.length;
      const v = e.target.value;
      const prevLength = inputLength;
      const deltaChars = l - prevLength; // Positive for additions, negative for deletions

      // Track typing event for rolling window WPM
      if (deltaChars !== 0 && raceStartTimeRef.current) {
        addTypingEvent(deltaChars);
        
        // Update total characters typed (only count additions, not deletions)
        if (deltaChars > 0) {
          totalCharsTypedRef.current += deltaChars;
        }

        // Calculate cumulative average WPM (more stable and accurate)
        const cumulativeWPM = calculateCumulativeWPM();
        
        // Use cumulative WPM for display (more stable), but keep live WPM for chart
        if (cumulativeWPM > 0) {
          const now = Date.now();
          const secondsFromStart = (now - raceStartTimeRef.current) / 1000;
          
          // Update player WPM with cumulative average (stable display)
          dispatch(updatePlayerWPM({ player_id: player_id, wpm: cumulativeWPM }));
          
          // Still track live WPM for chart visualization
          const liveWPM = calculateLiveWPM();
          if (liveWPM > 0) {
            dispatch(
              addLiveWPMDatapoint({
                timestamp: secondsFromStart,
                wpm: liveWPM,
              }),
            );
          }
        }
      }

      let wrongExists = false;
      setInputLength((prevL) => (prevL = l));
      setInputValue((prevV) => (prevV = v));
      let offset = l;
      let base = b_idx;
      const currentWordIdx = playerState.currentWord;
      if (currentWordIdx >= words.length) {
        return;
      }
      let maxCheck = base + words[currentWordIdx].length;
      let currentCharacterIdx = base + offset;
      if (currentCharacterIdx <= maxCheck) {
        const loop_limit = Math.min(base + maxCheck, currentCharacterIdx);
        const reset_loop_limit = Math.min(base + maxCheck, base + inputLength);
        const newArray = [...charArray];
        for (let i = base; i <= reset_loop_limit; i++) {
          newArray[i] = 0;
        }
        setCharArray((arr) => (arr = newArray));
        // Track if we've already recorded an error for this word in this input cycle
        let firstErrorRecorded = false;
        for (let i = base; i < loop_limit; i++) {
          if (chars[i] === v[i - base] && wrongExists === false) {
            newArray[i] = 1;
          } else if (chars[i] === v[i - base] && wrongExists === true) {
            newArray[i] = -1;
          } else {
            wrongExists = true;
            newArray[i] = -1;

            // Only record the first error per word to avoid redundant data
            if (!firstErrorRecorded && raceStartTimeRef.current) {
              const dateWrongWord = Date.now();
              // Use raceStartTimeRef which is already set when race starts (more reliable)
              const tmstmp = (dateWrongWord - raceStartTimeRef.current) / 1000;

              // Use live WPM instead of recalculating on error
              const currentWPM = calculateLiveWPM();

              // Store the actual incorrectly typed character (what user typed), not the expected one
              // chars[i] = expected character, v[i-base] = what user actually typed
              const typedChar =
                v[i - base] !== undefined && v[i - base] !== null
                  ? v[i - base]
                  : "";
              const expectedChar = chars[i];

              // Store what was typed incorrectly (or expected if typed is empty/undefined)
              // Ensure timestamp and wpm are valid numbers
              if (tmstmp >= 0 && !isNaN(tmstmp)) {
                const errorData = {
                  word: currentWordIdx,
                  incorrectChar: typedChar || expectedChar, // Store what was typed, fallback to expected
                  timestamp: tmstmp,
                  wpm: Math.max(0, currentWPM || 0), // Ensure WPM is non-negative
                };
                console.log(
                  "[GameInput] Dispatching incorrect word:",
                  errorData,
                );
                dispatch(setIncorrectRaceStatistic(errorData));

                firstErrorRecorded = true; // Prevent multiple dispatches for same word
              } else {
                console.log(
                  "[GameInput] Skipping error dispatch - invalid timestamp:",
                  { tmstmp, raceStartTimeRef: raceStartTimeRef.current },
                );
              }
            }
          }
        }
        setCharArray((arr) => (arr = newArray));
        newArray[currentCharacterIdx] = 1000;
        setCharArray((arr) => (arr = newArray));
      }
      const wordCompletion = v.toString() === words[currentWordIdx].join("");
      if (wordCompletion) {
        e.target.value = "";
        onWordCompletion();
      }
    };
    const handleClearInput = () => {
      setInputValue((prevV) => (prevV = ""));
    };
    useEffect(() => {
      if (countdownFlag === false && inputElement.current) {
        inputElement.current.focus();
      }
    }, [countdownFlag, inputElement]);
    useEffect(() => {
      setPracticeStart("");
      inputElement.current.value = "";
    }, [gameEnd, inputElement]);

    useEffect(() => {
      setBase(baseIdx);
    }, [baseIdx]);

    // Initialize race start time and reset character counter
    useEffect(() => {
      if (!notStart && countdownFlag === false && !raceStartTimeRef.current) {
        const timest =
          gameType === "PRACTICE"
            ? practiceStartTime
            : mStartTime
              ? deserify(mStartTime, defaultOptions)
              : null;
        if (timest) {
          raceStartTimeRef.current =
            timest instanceof Date
              ? timest.getTime()
              : new Date(timest).getTime();
          // Reset total characters typed when race starts
          totalCharsTypedRef.current = 0;
        }
      }
      return () => {
        if (liveWPMUpdateIntervalRef.current) {
          clearInterval(liveWPMUpdateIntervalRef.current);
          liveWPMUpdateIntervalRef.current = null;
        }
      };
    }, [notStart, countdownFlag, gameType, practiceStartTime, mStartTime]);

    // Ring buffer implementation for typing events
    const addTypingEvent = (deltaChars) => {
      const now = Date.now();
      typingEventsRef.current.push({
        timestampMs: now,
        deltaChars: deltaChars,
      });
      // Prune expired events (older than 2 seconds)
      const cutoff = now - WPM_WINDOW_MS;
      while (
        typingEventsRef.current.length > 0 &&
        typingEventsRef.current[0].timestampMs < cutoff
      ) {
        typingEventsRef.current.shift();
      }
    };

    // Calculate cumulative average WPM (total chars typed / total time elapsed)
    // This gives a stable, accurate WPM that represents overall performance
    const calculateCumulativeWPM = useCallback(() => {
      if (!raceStartTimeRef.current || totalCharsTypedRef.current === 0) {
        return 0;
      }
      
      const now = Date.now();
      const elapsedMs = now - raceStartTimeRef.current;
      const elapsedMinutes = elapsedMs / 60000; // Convert to minutes
      
      // Guard against very small time differences
      if (elapsedMinutes < 0.01) { // Less than 0.6 seconds
        return 0;
      }
      
      // WPM = (total characters / 5) / elapsed minutes
      const wpm = Math.max(0, Math.round((totalCharsTypedRef.current / 5) / elapsedMinutes));
      
      return wpm;
    }, []);
    
    // Calculate live WPM from rolling window (for chart visualization)
    const calculateLiveWPM = useCallback(() => {
      const now = Date.now();
      const cutoff = now - WPM_WINDOW_MS;

      // Prune expired events
      while (
        typingEventsRef.current.length > 0 &&
        typingEventsRef.current[0].timestampMs < cutoff
      ) {
        typingEventsRef.current.shift();
      }

      // Sum character deltas in window
      let charsInWindow = 0;
      for (const event of typingEventsRef.current) {
        if (event.timestampMs >= cutoff) {
          charsInWindow += Math.max(0, event.deltaChars); // Clamp at 0 for net WPM
        }
      }

      // Calculate WPM: (chars / 5) / (windowMinutes)
      // windowMinutes = 2000ms / 60000ms = 1/30 minutes
      // wpm = (charsInWindow / 5) * (60000 / 2000) = (charsInWindow / 5) * 30
      const windowMinutes = WPM_WINDOW_MS / 60000;
      const wpm =
        windowMinutes > 0
          ? Math.max(0, Math.round(charsInWindow / 5 / windowMinutes))
          : 0;

      return wpm;
    }, []);

    // Update live WPM periodically
    useEffect(() => {
      if (notStart || countdownFlag || gameEnd || !raceStartTimeRef.current) {
        if (liveWPMUpdateIntervalRef.current) {
          clearInterval(liveWPMUpdateIntervalRef.current);
          liveWPMUpdateIntervalRef.current = null;
        }
        return;
      }

      // Start periodic updates
      liveWPMUpdateIntervalRef.current = setInterval(() => {
        if (!raceStartTimeRef.current) {
          return;
        }
        
        // Use cumulative WPM for display (stable and accurate)
        const cumulativeWPM = calculateCumulativeWPM();
        if (cumulativeWPM > 0) {
          const now = Date.now();
          const secondsFromStart = (now - raceStartTimeRef.current) / 1000;

          // Update Redux with cumulative average WPM (stable display)
          dispatch(updatePlayerWPM({ player_id: player_id, wpm: cumulativeWPM }));

          // Still track live WPM for chart visualization (shows speed variations)
          const liveWPM = calculateLiveWPM();
          if (liveWPM > 0) {
            dispatch(
              addLiveWPMDatapoint({
                timestamp: secondsFromStart,
                wpm: liveWPM,
              }),
            );
          }
        }
      }, WPM_UPDATE_INTERVAL_MS);

      return () => {
        if (liveWPMUpdateIntervalRef.current) {
          clearInterval(liveWPMUpdateIntervalRef.current);
          liveWPMUpdateIntervalRef.current = null;
        }
      };
    }, [
      notStart,
      countdownFlag,
      gameEnd,
      player_id,
      dispatch,
      calculateLiveWPM,
      calculateCumulativeWPM,
    ]);

    // Reset typing events and counters when game ends
    useEffect(() => {
      if (gameEnd) {
        typingEventsRef.current = [];
        raceStartTimeRef.current = null;
        totalCharsTypedRef.current = 0; // Reset total characters
        if (liveWPMUpdateIntervalRef.current) {
          clearInterval(liveWPMUpdateIntervalRef.current);
          liveWPMUpdateIntervalRef.current = null;
        }
      }
    }, [gameEnd]);

    const endTime = (startTime, word_idx) => {
      if (!startTime || typeof word_idx !== "number" || word_idx < 0) {
        return 0;
      }

      let endTime = new Date();
      const startTimeMs =
        startTime instanceof Date
          ? startTime.getTime()
          : new Date(startTime).getTime();
      const timeDiffMs = endTime.getTime() - startTimeMs;
      const timeDiff = timeDiffMs / 1000 / 60; // in minutes

      // Guard against division by zero and very small time differences
      const MIN_TIME_SECONDS = 0.6; // Minimum 0.6 seconds for stable WPM
      if (timeDiffMs < MIN_TIME_SECONDS * 1000) {
        // Use live WPM if available, otherwise return 0
        const liveWPM = calculateLiveWPM();
        return liveWPM > 0 ? liveWPM : 0;
      }

      const wordsCompleted = word_idx + 1;
      let wpm = Math.round(wordsCompleted / timeDiff);

      // Smooth early game: apply exponential smoothing for first 10 words
      if (wordsCompleted <= 10) {
        const smoothingFactor = 0.7; // Weight for new value
        const previousWPM = playerState?.wpm || 0;
        wpm = Math.round(
          previousWPM * (1 - smoothingFactor) + wpm * smoothingFactor,
        );
      }

      // Clamp WPM to reasonable bounds (0-500)
      wpm = Math.max(0, Math.min(500, wpm));

      // Check for invalid values
      if (!isFinite(wpm) || isNaN(wpm)) {
        const liveWPM = calculateLiveWPM();
        return liveWPM > 0 ? liveWPM : 0;
      }

      dispatch(updatePlayerWPM({ player_id: player_id, wpm: wpm }));
      return wpm;
    };
    const setPracticeStartTime = () => {
      setPracticeStart((start) => (start = new Date()));
    };
    const gameEndHandler = () => {
      if (roomType !== "PRACTICE") {
        socket.emit("player finished", {
          player_id: multiplayer_id,
        });
      }
      dispatch(toggleNotStart());
      dispatch(updateGameStatus({ player_id: player_id }));
    };
    const endGameDetector = () => {
      // Guard: Ensure player state exists
      if (!playerState || typeof playerState.currentWord !== "number") {
        return false;
      }
      const nxtWord = playerState.currentWord + 1;
      if (nxtWord >= LIMIT) {
        return true;
      }
      return false;
    };
    const startPracticeTiming = () => {
      // Guard: Ensure player state exists
      if (!playerState || typeof playerState.currentWord !== "number") {
        return;
      }
      const firstTypeDetected =
        playerState.currentWord === 0 && practiceStartTime === "";
      if (firstTypeDetected) {
        setPracticeStartTime();
      }
    };
    const resetGameInput = () => {
      handleClearInput();
    };
    const onWordCompletion = () => {
      const timest =
        gameType === "PRACTICE"
          ? practiceStartTime
          : deserify(mStartTime, defaultOptions);
      const dateWordCompleted = new Date();
      const flag = endGameDetector();
      if (mStartTime) {
        // works for multiplayer games(hacky)
        mStartTime = deserify(mStartTime, defaultOptions);
      }
      // Guard: Ensure player state exists
      if (!playerState || typeof playerState.currentWord !== "number") {
        return;
      }
      let updated_wpm;
      if (gameType === "PRACTICE") {
        updated_wpm = endTime(practiceStartTime, playerState.currentWord);
      } else {
        updated_wpm = endTime(mStartTime, playerState.currentWord);
      }
      const timestamp = (dateWordCompleted - timest) / 1000;
      progressCurrCharIdx();
      const updateWord = playerState.currentWord + 1;
      dispatch(
        updatePlayerWord({ player_id: player_id, currWord: updateWord }),
      );
      if (room.gameType === "MULTIPLAYER") {
        socket.emit("update player state", {
          player_id: multiplayer_id,
          wpm: updated_wpm,
          progress: updateWord,
        });
      }
      dispatch(
        setProgressRaceStatistic({
          word: playerState.currentWord,
          secondTyped: timestamp,
          wordWPM: updated_wpm,
        }),
      );
      if (flag === true) {
        if (roomType === "PRACTICE") {
          dispatch(setPracticeWPM(updated_wpm));
        } else if (roomType === "MULTIPLAYER") {
          dispatch(setMultiplayerWPM(updated_wpm));
        }
        gameEndHandler();
      }
    };
    const gameDoneChecker = () => {
      // Guard: Ensure player state exists
      if (!playerState || typeof playerState.currentWord !== "number") {
        return false;
      }
      const gameDone = playerState.currentWord >= LIMIT;
      if (gameDone) {
        return true;
      } else {
        return false;
      }
    };
    return (
      <section>
        <input
          ref={inputElement}
          autoComplete="off"
          className="game-input"
          type="text"
          id="userInput"
          placeholder="Type Here..."
          disabled={gameEnd || notStart}
          onChange={(e) => {
            handleInputChange(e);
            if (gameEnd || notStart) {
              resetGameInput();
              if (inputElement.current) {
                inputElement.current.disabled = true;
              }
              return;
            }
            startPracticeTiming();
            if (gameDoneChecker() === true) {
              return;
            }
          }}
        />
      </section>
    );
  },
);

export default GameInput;
